// Auto-generated lightweight class for PartDefinition
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { PartDefinitionReadAPI, PartDefinitionWriteAPI, PartDefinitionReadAPISchema, PartDefinitionWriteAPISchema } from '../types/partdefinition';
import { ModelRegistry } from '../../registries/model-registry';
import { Allocation, AtScope, HasMembers, PartComposedOf, Performs, Specializes } from '../../relationships/relationship';

import { ActionDefinition } from './actiondefinition';
import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from './sysmlnamespace';
import { SysMLType } from './sysmltype';



export interface PartDefinition extends Partial<PartDefinitionReadAPI> {}
export class PartDefinition extends NeoNodeBase<PartDefinitionReadAPI, PartDefinitionWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "PartDefinition";
  static readonly readSchema = PartDefinitionReadAPISchema;
  static readonly writeSchema = PartDefinitionWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    general: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "out" },
    specific: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "in" },
    allocated_actions: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "Allocation", direction: "in" },
    performed_actions: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "Performs", direction: "out" },
    sub_parts: { target: "PartDefinition", cardinality: "many", isParent: false, relClass: "PartComposedOf", direction: "out" },
    parent_part: { target: "PartDefinition", cardinality: "one", isParent: true, relClass: "PartComposedOf", direction: "in" },
  };

  protected get modelName(): string {
    return 'PartDefinition';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return PartDefinition.label;
  }


  constructor(data: Partial<PartDefinitionWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: PartDefinitionReadAPI, loadContext?: Record<string, any>): PartDefinition {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { allocated_actions, performed_actions, sub_parts, parent_part, security_scope, member_namespace, general, specific, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = PartDefinitionReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, allocated_actions: allocated_actions, performed_actions: performed_actions, sub_parts: sub_parts, parent_part: parent_part, security_scope: security_scope, member_namespace: member_namespace, general: general, specific: specific };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return PartDefinition.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'allocated_actions', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'performed_actions', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'sub_parts', 'PartDefinition', ModelRegistry);
    loadManyRelationship(processed, 'parent_part', 'PartDefinition', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadManyRelationship(processed, 'general', 'SysMLType', ModelRegistry);
    loadManyRelationship(processed, 'specific', 'SysMLType', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new PartDefinition(processed as any);

    return instance;
  }


  // Relationship manager for 'allocated_actions' - Python ORM style (cached)
  get allocated_actions(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('allocated_actions', () => {
      return new RelationshipManager(this, 'allocated_actions', 'many', Allocation as typeof Relationship);
    });
  }

  // Relationship manager for 'performed_actions' - Python ORM style (cached)
  get performed_actions(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('performed_actions', () => {
      return new RelationshipManager(this, 'performed_actions', 'many', Performs as typeof Relationship);
    });
  }

  // Relationship manager for 'sub_parts' - Python ORM style (cached)
  get sub_parts(): RelationshipManager<this, PartDefinition> {
    return this.getOrCreateRelationshipManager('sub_parts', () => {
      return new RelationshipManager(this, 'sub_parts', 'many', PartComposedOf as typeof Relationship);
    });
  }

  // Relationship manager for 'ports' - Python ORM style (cached)
  get ports(): RelationshipManager<this, any> {
    return this.getOrCreateRelationshipManager('ports', () => {
      return new RelationshipManager(this, 'ports', 'many');
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'general' - Python ORM style (cached)
  get general(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('general', () => {
      return new RelationshipManager(this, 'general', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'specific' - Python ORM style (cached)
  get specific(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('specific', () => {
      return new RelationshipManager(this, 'specific', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'parent_part' - Python ORM style (cached)
  get parent_part(): RelationshipManager<this, PartDefinition> {
    return this.getOrCreateRelationshipManager('parent_part', () => {
      return new RelationshipManager(this, 'parent_part', 'one', PartComposedOf as typeof Relationship);
    });
  }

}
